import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  So5LeaderboardSeasonality,
  So5LeaderboardType,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { DivisionLogo } from '@sorare/core/src/components/leaderboard/DivisionLogo';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { SeasonalityLabel } from 'components/lineup/SeasonalityLabel';

import type { So5LeaderboardHeader_so5Leaderboard } from './__generated__/index.graphql';

type Props = {
  so5Leaderboard: So5LeaderboardHeader_so5Leaderboard;
};
export const So5LeaderboardHeader = ({ so5Leaderboard }: Props) => {
  const { sport } = useSportContext();

  const leagueName =
    so5Leaderboard.so5TournamentType.so5LeaderboardType ===
    So5LeaderboardType.SPECIAL_WEEKLY
      ? 'Special Event'
      : so5Leaderboard.so5LeaderboardGroup.displayName;
  const seasonalityLabel =
    so5Leaderboard.so5TournamentType.so5LeaderboardType.includes('GLOBAL_CAP')
      ? so5Leaderboard.displayName
      : leagueName;

  return (
    <Horizontal>
      <DivisionLogo so5Leaderboard={so5Leaderboard} />
      <Vertical gap={0}>
        <BodyS as="p" bold>
          {sport === Sport.FOOTBALL
            ? so5Leaderboard.so5LeaderboardGroup.displayName
            : so5Leaderboard.displayName}
        </BodyS>
        {sport === Sport.FOOTBALL && (
          <BodyS as="p">
            <SeasonalityLabel
              seasonality={
                so5Leaderboard.seasonality ||
                (so5Leaderboard.so5TournamentType.so5LeaderboardType ===
                So5LeaderboardType.SPECIAL_WEEKLY
                  ? So5LeaderboardSeasonality.IN_SEASON
                  : So5LeaderboardSeasonality.ALL_SEASONS)
              }
              label={
                so5Leaderboard.seasonality
                  ? so5Leaderboard.seasonalityName
                  : seasonalityLabel
              }
            />
          </BodyS>
        )}
      </Vertical>
    </Horizontal>
  );
};

So5LeaderboardHeader.fragments = {
  so5Leaderboard: gql`
    fragment So5LeaderboardHeader_so5Leaderboard on So5Leaderboard {
      slug
      seasonality
      seasonalityName
      displayName
      so5LeaderboardGroup {
        slug
        displayName
      }
      so5TournamentType {
        id
        so5LeaderboardType
      }
      ...DivisionLogo_so5Leaderboard
    }
    ${DivisionLogo.fragments.so5Leaderboard}
  ` as TypedDocumentNode<So5LeaderboardHeader_so5Leaderboard>,
};
